/* eslint-disable no-console */
import {MinusCircleFilled, PlusCircleFilled} from '@ant-design/icons';
import {Checkbox} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useState} from 'react';

import {
  extractPrefixSet,
  returnSingleNewLabel
} from '../Constants/commonFunctions';
import getLabelsFromSelectedPermissions from '../Constants/getLabelFromPermission';

import {PostFormData} from './UserManager';

interface PermissionItem {
  value: string;
  description: string;
}

interface PermissionObject {
  [key: string]: PermissionItem[] | PermissionObject;
}

interface ModulePermissionAccordianProps {
  allPermissions: PermissionObject;
  onPostDataChange: any;
  postFormData: PostFormData;
}

const ModulePermissionAccordian: React.FC<ModulePermissionAccordianProps> = ({
  allPermissions,
  onPostDataChange,
  postFormData
}) => {
  const {setFieldValue} = useFormInstance();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    const currentPermissions = postFormData.permissions ?? [];
    const updatedPermissions = checked
      ? [...currentPermissions, permission]
      : currentPermissions.filter((item) => item !== permission);
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, updatedPermissions)
    );
    onPostDataChange({permissions: updatedPermissions});
  };

  const collectAllPaths = (data: PermissionObject, basePath = ''): string[] => {
    const paths: string[] = [];
    Object.entries(data).forEach(([key, value]) => {
      const currentPath = basePath ? `${basePath}:${key}` : key;
      if (Array.isArray(value)) {
        paths.push(currentPath);
      } else if (typeof value === 'object') {
        paths.push(...collectAllPaths(value, currentPath));
      }
    });

    return paths;
  };

  const handleSelectAll = (isChecked: boolean) => {
    const allPaths = collectAllPaths(allPermissions);
    const updatedPaths = updateAllPermissions(allPaths, isChecked);
    setExpandedSections(extractPrefixSet(updatedPaths));
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, updatedPaths)
    );
  };

  const handleSelectGroup = (
    groupPath: string,
    groupData: PermissionObject,
    isChecked: boolean
  ) => {
    const groupPaths = collectAllPaths(groupData, groupPath);
    const updatedPaths = updateAllPermissions(groupPaths, isChecked);
    setExpandedSections(extractPrefixSet(updatedPaths));
    setFieldValue(
      'selectedPermissions',
      getLabelsFromSelectedPermissions(allPermissions, updatedPaths)
    );
  };

  const updateAllPermissions = (
    paths: string[],
    isChecked: boolean
  ): string[] => {
    const updatedPermissions = new Set(postFormData.permissions ?? []);
    paths.forEach((path) => {
      if (isChecked) {
        updatedPermissions.add(path);
      } else {
        updatedPermissions.delete(path);
      }
    });
    const permissionArray = Array.from(updatedPermissions);
    onPostDataChange({permissions: Array.from(updatedPermissions)});

    return permissionArray;
  };

  const areAllPermissionsSelected = (
    data: PermissionObject,
    basePath = ''
  ): boolean => {
    const allPaths = collectAllPaths(data, basePath);

    return allPaths.every((path) => postFormData.permissions?.includes(path));
  };

  const renderPermissions = (data: PermissionObject, path = '') => {
    return (
      <div>
        {/* Group-level "Select All" checkbox */}
        {path === '' && (
          <div style={{marginBottom: '10px'}}>
            <Checkbox
              checked={areAllPermissionsSelected(data)}
              onChange={(e) => {
                handleSelectAll(e.target.checked);
              }}
            >
              Select All Permissions
            </Checkbox>
          </div>
        )}
        {Object.entries(data).map(([key, value]) => {
          const currentPath = path ? `${path}:${key}` : key;
          const isExpanded = expandedSections[currentPath];

          if (Array.isArray(value) && value.length > 0) {
            const firstItem = value[0];

            return (
              <div
                key={currentPath}
                style={{
                  border: '1px solid #D2D4DA',
                  borderRadius: '10px',
                  marginBottom: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '5px 10px',
                    // border: '1px solid #D2D4DA',
                    borderRadius: '5px'
                  }}
                >
                  <p>{returnSingleNewLabel(firstItem.description, true)}</p>
                  <Checkbox
                    checked={postFormData?.permissions?.includes(currentPath)}
                    onChange={(e) => {
                      handlePermissionChange(currentPath, e.target.checked);
                    }}
                  />
                </div>
              </div>
            );
          }

          if (typeof value === 'object' && !Array.isArray(value)) {
            return (
              <div
                key={currentPath}
                style={{
                  border: '1px solid #D2D4DA',
                  borderRadius: '10px',
                  marginBottom: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px', // Adds spacing between checkbox and label
                      flexGrow: 1 // Pushes the name to the left
                    }}
                  >
                    <Checkbox
                      checked={areAllPermissionsSelected(value, currentPath)}
                      onChange={(e) => {
                        handleSelectGroup(currentPath, value, e.target.checked);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }} // Prevents click conflict with toggleSection
                    />
                    {returnSingleNewLabel(key)}
                  </div>
                  <span
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onClick={() => {
                      toggleSection(currentPath);
                    }}
                  >
                    {isExpanded ? (
                      <MinusCircleFilled
                        style={{
                          color: '#435B37',
                          fontSize: '20px',
                          marginLeft: '10px'
                        }}
                      />
                    ) : (
                      <PlusCircleFilled
                        style={{
                          color: '#435B37',
                          fontSize: '20px',
                          marginLeft: '10px'
                        }}
                      />
                    )}
                  </span>
                </div>
                {isExpanded && (
                  <div style={{paddingLeft: '16px', paddingRight: '16px'}}>
                    {renderPermissions(value, currentPath)}
                  </div>
                )}
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  return (
    <div className="heloo">
      {allPermissions && renderPermissions(allPermissions)}
    </div>
  );
};

export default ModulePermissionAccordian;
